/**
 * useShiftPlanning — Infinite-scroll shift planning data loader.
 *
 * Loads shift planning grid data month-by-month as the user scrolls.
 * Data is stored in a Map keyed by month (YYYY-MM) and exposed as a
 * flat sorted array of ShiftPlanningDate objects.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiFetch } from '../lib/api';
import { computeBattalionForDate } from '../lib/schedule';

// ── Types ────────────────────────────────────────────────────────────────

export interface ShiftAssignment {
  station_id: string;
  unit_id: string;
  seat_id: string;
  member_id: number | null;
  display_name: string | null;
  is_override: boolean;
  is_required: boolean;
}

export interface CoverageRequest {
  id: number;
  status: string;
  position_needed: string | null;
  duty_schedule_id: number;
  requesting_member_id: number;
}

export interface ShiftPlanningDate {
  date: string;
  duty_schedule_id: number;
  battalion_id: number;
  battalion_number: number;
  shift_start: string;
  shift_end: string;
  is_holiday: boolean;
  assignments: ShiftAssignment[];
  coverage_requests: CoverageRequest[];
}

interface ShiftPlanningResponse {
  dates: ShiftPlanningDate[];
}

export interface AssignParams {
  date: string;
  battalion_id: number;
  unit_id: string;
  seat_id: string;
  member_id: number;
}

export interface UseShiftPlanningReturn {
  /** Flat sorted array of all loaded dates */
  dates: ShiftPlanningDate[];
  /** True during initial load only */
  loading: boolean;
  /** True when loading additional months to the right */
  loadingFuture: boolean;
  /** True when loading additional months to the left */
  loadingPast: boolean;
  error: string | null;
  battalionFilter: number | null;
  setBattalionFilter: (id: number | null) => void;
  /** Load the next month of data (scrolling right) */
  loadMore: (direction: 'future' | 'past') => void;
  /** Whether there's more data available in each direction */
  hasMoreFuture: boolean;
  hasMorePast: boolean;
  assignMember: (params: AssignParams) => Promise<void>;
  removeOverride: (params: Omit<AssignParams, 'member_id'>) => Promise<void>;
  refresh: () => void;
  /** Load a specific month by date string (YYYY-MM-DD), filling gaps if needed */
  loadMonth: (dateStr: string) => Promise<void>;
}

// ── Helpers ──────────────────────────────────────────────────────────────

/** Get the first day of a month */
function monthStart(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-01`;
}

/** Get the last day of a month */
function monthEnd(year: number, month: number): string {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

/** Parse YYYY-MM into { year, month (0-indexed) } */
function parseMonthKey(key: string): { year: number; month: number } {
  const [y, m] = key.split('-').map(Number);
  return { year: y, month: m - 1 };
}

/** Get YYYY-MM key for a year/month (0-indexed) */
function toMonthKey(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

/** Advance a month key by N months (positive or negative) */
function advanceMonth(key: string, n: number): string {
  const { year, month } = parseMonthKey(key);
  const d = new Date(year, month + n, 1);
  return toMonthKey(d.getFullYear(), d.getMonth());
}

// ── Hook ─────────────────────────────────────────────────────────────────

/** Maximum months into the future from today */
const MAX_FUTURE_MONTHS = 12;
/** Maximum months into the past from today */
const MAX_PAST_MONTHS = 6;

export function useShiftPlanning(): UseShiftPlanningReturn {
  const todayDate = new Date();
  const currentMonthKey = toMonthKey(todayDate.getFullYear(), todayDate.getMonth());

  // Month data store: Map<YYYY-MM, ShiftPlanningDate[]>
  const [monthData, setMonthData] = useState<Map<string, ShiftPlanningDate[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadingFuture, setLoadingFuture] = useState(false);
  const [loadingPast, setLoadingPast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [battalionFilter, setBattalionFilter] = useState<number | null>(null);

  // Track which months have been loaded or are loading
  const loadedMonthsRef = useRef<Set<string>>(new Set());
  const loadingMonthsRef = useRef<Set<string>>(new Set());

  // Track the earliest and latest loaded month keys
  const [earliestMonth, setEarliestMonth] = useState(currentMonthKey);
  const [latestMonth, setLatestMonth] = useState(currentMonthKey);

  // Compute limits
  const minMonthKey = useMemo(() => {
    return advanceMonth(currentMonthKey, -MAX_PAST_MONTHS);
  }, [currentMonthKey]);

  const maxMonthKey = useMemo(() => {
    return advanceMonth(currentMonthKey, MAX_FUTURE_MONTHS);
  }, [currentMonthKey]);

  const hasMoreFuture = latestMonth < maxMonthKey;
  const hasMorePast = earliestMonth > minMonthKey;

  // Fetch a single month's data
  const fetchMonth = useCallback(
    async (monthKey: string, signal?: AbortSignal): Promise<ShiftPlanningDate[] | null> => {
      if (loadedMonthsRef.current.has(monthKey) || loadingMonthsRef.current.has(monthKey)) return null;
      loadingMonthsRef.current.add(monthKey);

      const { year, month } = parseMonthKey(monthKey);
      const start = monthStart(year, month);
      const end = monthEnd(year, month);

      let url = `/api/shift-planning?start=${start}&end=${end}`;
      if (battalionFilter !== null) {
        url += `&battalion_id=${battalionFilter}`;
      }

      try {
        const data = await apiFetch<ShiftPlanningResponse>(url, { signal });
        loadedMonthsRef.current.add(monthKey);
        loadingMonthsRef.current.delete(monthKey);
        return data.dates;
      } catch (err) {
        loadingMonthsRef.current.delete(monthKey);
        if (signal?.aborted) return null;
        throw err;
      }
    },
    [battalionFilter],
  );

  // Initial load: current month + next month
  useEffect(() => {
    const controller = new AbortController();
    loadedMonthsRef.current.clear();
    loadingMonthsRef.current.clear();
    setLoading(true);
    setError(null);
    setMonthData(new Map());

    const nextMonthKey = advanceMonth(currentMonthKey, 1);

    Promise.all([fetchMonth(currentMonthKey, controller.signal), fetchMonth(nextMonthKey, controller.signal)])
      .then(([currentData, nextData]) => {
        if (controller.signal.aborted) return;
        const newMap = new Map<string, ShiftPlanningDate[]>();
        if (currentData) newMap.set(currentMonthKey, currentData);
        if (nextData) newMap.set(nextMonthKey, nextData);
        setMonthData(newMap);
        setEarliestMonth(currentMonthKey);
        setLatestMonth(nextMonthKey);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to load shift planning data');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battalionFilter]);

  // Load more months in a direction
  const loadMore = useCallback(
    (direction: 'future' | 'past') => {
      if (direction === 'future') {
        if (!hasMoreFuture || loadingFuture) return;
        const nextKey = advanceMonth(latestMonth, 1);
        if (nextKey > maxMonthKey) return;

        setLoadingFuture(true);
        fetchMonth(nextKey)
          .then((data) => {
            if (data) {
              setMonthData((prev) => {
                const next = new Map(prev);
                next.set(nextKey, data);
                return next;
              });
              setLatestMonth(nextKey);
            }
          })
          .catch(() => {
            /* best-effort */
          })
          .finally(() => setLoadingFuture(false));
      } else {
        if (!hasMorePast || loadingPast) return;
        const prevKey = advanceMonth(earliestMonth, -1);
        if (prevKey < minMonthKey) return;

        setLoadingPast(true);
        fetchMonth(prevKey)
          .then((data) => {
            if (data) {
              setMonthData((prev) => {
                const next = new Map(prev);
                next.set(prevKey, data);
                return next;
              });
              setEarliestMonth(prevKey);
            }
          })
          .catch(() => {
            /* best-effort */
          })
          .finally(() => setLoadingPast(false));
      }
    },
    [
      hasMoreFuture,
      hasMorePast,
      loadingFuture,
      loadingPast,
      latestMonth,
      earliestMonth,
      maxMonthKey,
      minMonthKey,
      fetchMonth,
    ],
  );

  // Flat sorted dates array — override battalion_number with client-side
  // computation so Shift Planning stays consistent with the sidebar and
  // Duty Schedule page regardless of DB state.
  const dates = useMemo(() => {
    const allDates: ShiftPlanningDate[] = [];
    // Sort month keys and flatten
    const sortedKeys = Array.from(monthData.keys()).sort();
    for (const key of sortedKeys) {
      const monthDates = monthData.get(key);
      if (monthDates) allDates.push(...monthDates);
    }
    return allDates.map((d) => ({
      ...d,
      battalion_number: computeBattalionForDate(d.date).battalion,
    }));
  }, [monthData]);

  const refresh = useCallback(() => {
    // Clear loaded months and re-trigger initial load by updating battalion filter to same value
    loadedMonthsRef.current.clear();
    loadingMonthsRef.current.clear();

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    // Reload all currently loaded months
    const monthKeys = Array.from(monthData.keys()).sort();
    if (monthKeys.length === 0) return;

    Promise.all(
      monthKeys.map(async (key) => {
        const { year, month } = parseMonthKey(key);
        const start = monthStart(year, month);
        const end = monthEnd(year, month);
        let url = `/api/shift-planning?start=${start}&end=${end}`;
        if (battalionFilter !== null) url += `&battalion_id=${battalionFilter}`;
        const data = await apiFetch<ShiftPlanningResponse>(url, { signal: controller.signal });
        loadedMonthsRef.current.add(key);
        return { key, dates: data.dates };
      }),
    )
      .then((results) => {
        if (controller.signal.aborted) return;
        const newMap = new Map<string, ShiftPlanningDate[]>();
        for (const r of results) {
          newMap.set(r.key, r.dates);
        }
        setMonthData(newMap);
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to refresh shift data');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, [monthData, battalionFilter]);

  const assignMember = useCallback(
    async (params: AssignParams) => {
      await apiFetch<{ success: boolean }>('/api/shift-planning/assign', {
        method: 'POST',
        body: JSON.stringify(params),
      });
      refresh();
    },
    [refresh],
  );

  const loadMonth = useCallback(
    async (dateStr: string) => {
      const d = new Date(dateStr + 'T12:00:00');
      const targetKey = toMonthKey(d.getFullYear(), d.getMonth());
      if (loadedMonthsRef.current.has(targetKey)) return;

      // Determine all month keys between earliest/latest and the target
      const keysToLoad: string[] = [];
      if (targetKey < earliestMonth) {
        let key = advanceMonth(earliestMonth, -1);
        while (key >= targetKey && key >= minMonthKey) {
          if (!loadedMonthsRef.current.has(key)) keysToLoad.push(key);
          key = advanceMonth(key, -1);
        }
      } else if (targetKey > latestMonth) {
        let key = advanceMonth(latestMonth, 1);
        while (key <= targetKey && key <= maxMonthKey) {
          if (!loadedMonthsRef.current.has(key)) keysToLoad.push(key);
          key = advanceMonth(key, 1);
        }
      } else {
        if (!loadedMonthsRef.current.has(targetKey)) keysToLoad.push(targetKey);
      }

      if (keysToLoad.length === 0) return;

      const results = await Promise.all(
        keysToLoad.map(async (key) => {
          const { year, month } = parseMonthKey(key);
          const start = monthStart(year, month);
          const end = monthEnd(year, month);
          let url = `/api/shift-planning?start=${start}&end=${end}`;
          if (battalionFilter !== null) url += `&battalion_id=${battalionFilter}`;
          const data = await apiFetch<ShiftPlanningResponse>(url);
          loadedMonthsRef.current.add(key);
          return { key, dates: data.dates };
        }),
      );

      setMonthData((prev) => {
        const next = new Map(prev);
        for (const r of results) next.set(r.key, r.dates);
        return next;
      });

      // Update earliest/latest bounds
      const allKeys = keysToLoad.sort();
      if (allKeys[0] < earliestMonth) setEarliestMonth(allKeys[0]);
      if (allKeys[allKeys.length - 1] > latestMonth) setLatestMonth(allKeys[allKeys.length - 1]);
    },
    [earliestMonth, latestMonth, minMonthKey, maxMonthKey, battalionFilter],
  );

  const removeOverride = useCallback(
    async (params: Omit<AssignParams, 'member_id'>) => {
      await apiFetch<{ success: boolean }>('/api/shift-planning/assign', {
        method: 'DELETE',
        body: JSON.stringify(params),
      });
      refresh();
    },
    [refresh],
  );

  return {
    dates,
    loading,
    loadingFuture,
    loadingPast,
    error,
    battalionFilter,
    setBattalionFilter,
    loadMore,
    hasMoreFuture,
    hasMorePast,
    assignMember,
    removeOverride,
    refresh,
    loadMonth,
  };
}
